import type React from 'react';

import { useGetEvent } from '@vlabs/api/artemy/client';
import { SearchIcon } from '@vlabs/icons';
import { DropdownMenu } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { routes } from '@vlapps/point3/src/app/routes';
import qs from 'qs';
import { useHistory, generatePath } from 'react-router-dom';

import { EVENT_TARGET } from '@vlabs/monokit/entities/events/api';

import { useFindSimilarDropdownTranslation } from './i18n';
import type { FindSimilarProps } from './types';
import { buildTestid } from '../../../shared/lib/utils';

type FindSimilarDropdownPoint3Props = FindSimilarProps & WithDataTestid;

const displayName = 'FindSimilarDropdownPoint3';
const testid = buildTestid(displayName);

function FindSimilarDropdownPoint3({
  eventId,
  type,
  'data-testid': dataTestId = undefined,
}: FindSimilarDropdownPoint3Props): React.JSX.Element {
  const { t } = useFindSimilarDropdownTranslation();
  const history = useHistory();

  const { data: event } = useGetEvent(eventId, { target: EVENT_TARGET });

  const onClick = (findType: 'event' | 'face') => {
    if (type === 'top match') {
      const topMatch = event?.top_match;

      if (!topMatch) return;

      const reference = 'face_id' in topMatch
        ? {
          id: topMatch.face_id,
          type: 'face',
          referenceType: 'face',
        }
        : {
          id: topMatch.event_id,
          type: 'event',
          referenceType: 'event',
        };

      history.push({
        pathname: generatePath(routes.search),
        search: qs.stringify({
          references: [
            {
              ...reference,
              candidateOrigin: findType === 'event' ? 'events' : 'faces',
            },
          ],
        }),
      });
    }

    if (type === 'detection') {
      history.push({
        pathname: generatePath(routes.search),
        search: qs.stringify({
          references: [
            {
              id: event?.event_id,
              type: 'event',
              referenceType: 'event',
              candidateOrigin: findType === 'event' ? 'events' : 'faces',
            },
          ],
        }),
      });
    }
  };

  return (
    <DropdownMenu.Root {...testid(dataTestId)}>
      <DropdownMenu.Trigger
        render={(
          <Button
            disabled={!event}
            icon={<SearchIcon />}
            kind="secondary"
            size="small"
            variant="filled"
            {...testid('DropdownMenuTriggerButton', dataTestId)}
          >
            {t('find_similar')}
          </Button>
        )}
      />

      <DropdownMenu.Portal>
        <DropdownMenu.Positioner side="bottom">
          <DropdownMenu.Popup {...testid('FindSimilarDropdown.DropdownMenuPopup')}>
            <DropdownMenu.Item
              onClick={() => onClick('event')}
              {...testid('DropdownMenuItemEvents', dataTestId)}
            >
              {t('events')}
            </DropdownMenu.Item>

            <DropdownMenu.Item
              onClick={() => onClick('face')}
              {...testid('DropdownMenuItemFaces', dataTestId)}
            >
              {t('faces')}
            </DropdownMenu.Item>
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

FindSimilarDropdownPoint3.displayName = displayName;

export { FindSimilarDropdownPoint3 };
